/**
 * Converts an underscored keyword to a display string with spaces.
 * e.g. "bunny_hop" → "bunny hop"
 *
 * Typesense keywords are stored with underscores to prevent unwanted
 * tokenization of multi-word terms. This function reverses that for display.
 */
export const formatKeywordForDisplay = (keyword: string): string =>
  keyword.replace(/_/g, ' ');

/**
 * Converts a keyword to the underscored format used in Typesense.
 * e.g. "bunny hop" → "bunny_hop"
 *
 * Spaces in multi-word keywords are replaced with underscores so Typesense
 * treats the entire keyword as a single token during filtering.
 */
export const formatKeywordForQuery = (keyword: string): string =>
  keyword.trim().replace(/\s+/g, '_');

/**
 * Normalises a keyword for case- and whitespace-insensitive comparison.
 * e.g. " Redemption " → "redemption"
 */
export const normalizeKeyword = (keyword: string): string =>
  keyword.toLowerCase().trim();

/**
 * Removes duplicate keywords, comparing case-insensitively and ignoring
 * surrounding whitespace. The first occurrence is kept and the original order
 * is preserved. Empty/whitespace-only entries are dropped.
 */
export const dedupeKeywords = (keywords: string[]): string[] => {
  const seen = new Set<string>();

  return keywords.filter((keyword) => {
    const normalized = normalizeKeyword(keyword);

    if (!normalized || seen.has(normalized)) {
      return false;
    }

    seen.add(normalized);
    return true;
  });
};
